import { getAreBackordersEnabled, getDefaultImageSize, getProductsGridSize } from '@app/ducks';
import { getGallerySlides, getHasAvailableVariants, getNativeVariants, getProductAvailability, getProductFamily } from '@app/ducks/helpers';
import { isVariantOrMultivariantRelation } from '@app/logic/merchandise';
import { filterProductRelatedProducts, GetProductUrl } from '@app/logic/products';
import { useClickOutside, useEventListener, useUrlProductId } from '@app/utils/hooks';
import { useContainsActiveElement } from '@app/utils/hooks/useContainsActiveElement';
import { useToggleInOrder } from '@app/utils/hooks/useToggleInOrder';
import { isDefined, isNull } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';

interface Props {
  productInfo: Product.Standard;
  getProductUrl: GetProductUrl;
  onAddToSelection?: VoidFunction;
}

interface Context {
  productInfo: Product.Standard;
  activeSlideIndex: number;
  containerRef: React.RefObject<HTMLDivElement>;
  gallerySlides: Product.GallerySlide[];
  hasAvailableVariants: boolean;
  isProductInOrder: boolean;
  hasUnits: boolean;
  isAdding: boolean;
  isLoading: boolean;
  isRemoving: boolean;
  areAllRelationsUnavailable: boolean;
  isMutating: boolean;
  toggleInOrder: () => void;
  onAddToSelection?: VoidFunction;
  hasHoverOrFocus: boolean;
  isProductAvailable: boolean;
  isVariantAvailable: boolean;
  setActiveSlideIndex: (index: number) => void;
  containerBottomOffset: number;
  setContainerBottomOffset: (value: number) => void;
  activeSlideVariant: Product.Standard | undefined;
  productPathName: string;
  productsGridSize: 'small' | 'medium' | 'large';
}

export const ProductItemContext = React.createContext<Context | null>(null);

export const useProductItemContext = (): Context => {
  const context = React.useContext(ProductItemContext);
  if (isNull(context)) {
    throw new Error('ProductItemContext can not be used outside the scope of ProductItemContextProvider');
  }

  return context;
};

export const ProductItemContextProvider = ({ children, productInfo, getProductUrl, onAddToSelection }: React.WithChildren<Props>) => {
  const defaultImageSize = useSelector(getDefaultImageSize);
  const productsGridSize = useSelector(getProductsGridSize);
  const areBackordersEnabled = useSelector(getAreBackordersEnabled);
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [containerBottomOffset, setContainerBottomOffset] = React.useState(0);
  const [isOverProduct, setIsOverProduct] = React.useState(false);
  const { containerRef, hasActiveElement } = useContainsActiveElement<HTMLDivElement>();
  const { isInOrder, hasUnits, isAdding, isLoading, isRemoving, toggleInOrder } = useToggleInOrder({
    product: productInfo,
  });
  const productInfoWithFilteredVariants = filterProductRelatedProducts(isVariantOrMultivariantRelation)(productInfo);
  const gallerySlides = getGallerySlides(productInfoWithFilteredVariants, defaultImageSize);
  const productFamily = getProductFamily(productInfoWithFilteredVariants);
  const hasAvailableVariants = getHasAvailableVariants(productFamily);

  const urlProductId = useUrlProductId();
  const activeSlideVariant = gallerySlides[activeSlideIndex]?.variant;
  const productPathName = getProductUrl({ productId: activeSlideVariant?.product, variantId: activeSlideVariant?.variant });
  const isProductModalVisible = isDefined(urlProductId) && !isEmpty(urlProductId);
  const hasActiveVariantAvailability = React.useMemo(
    () => isDefined(activeSlideVariant) && getProductAvailability(activeSlideVariant),
    [activeSlideVariant],
  );
  const nativeVariants = getNativeVariants(productInfo, productInfo.product);
  const hasAvailableNativeVariants = getHasAvailableVariants(nativeVariants);
  const isProductAvailable = hasAvailableNativeVariants || areBackordersEnabled;
  const isVariantAvailable = hasActiveVariantAvailability || areBackordersEnabled;
  const areAllRelationsUnavailable = !hasAvailableVariants && !areBackordersEnabled;
  const handleMouseOver = React.useCallback(() => {
    setIsOverProduct(true);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    setIsOverProduct(false);
    setActiveSlideIndex(0);
  }, []);

  useClickOutside({
    element: containerRef.current,
    enabled: !isProductModalVisible,
    handler: handleMouseLeave,
  });

  useEventListener({
    element: containerRef,
    eventName: 'mouseover',
    handler: handleMouseOver,
    isDisabled: isOverProduct,
  });

  useEventListener({
    element: containerRef,
    eventName: 'mouseleave',
    handler: handleMouseLeave,
    isDisabled: !isOverProduct || isProductModalVisible,
  });
  const isMutating = isRemoving || isAdding;

  return (
    <ProductItemContext.Provider
      value={{
        activeSlideIndex,
        activeSlideVariant,
        areAllRelationsUnavailable,
        containerBottomOffset,
        containerRef,
        gallerySlides,
        hasAvailableVariants,
        hasHoverOrFocus: isOverProduct || hasActiveElement,
        hasUnits,
        isAdding,
        isLoading,
        isMutating,
        isProductAvailable,
        isProductInOrder: isInOrder,
        isRemoving,
        isVariantAvailable,
        onAddToSelection,
        productInfo,
        productPathName,
        productsGridSize,
        setActiveSlideIndex,
        setContainerBottomOffset,
        toggleInOrder,
      }}
    >
      {children}
    </ProductItemContext.Provider>
  );
};
